import type { ProcessStepConnection } from '@/api/processStepsApi'

// ─── Lot allocation + reservation summary (pure) ─────────────────────────────
//
// The arithmetic and wording behind the process step's Inventory tab and its
// confirm-and-sign screen. Kept free of React and network imports so vitest can
// cover it — the config matches `src/**/*.test.ts` only, never `.tsx`, which is
// why anything worth testing has to live in a module like this one rather than
// beside the component that renders it.

/** The signed transitions a process step offers. */
export type TransitionMode = 'start' | 'end' | 'complete'

/**
 * Quantity per lot number as the allocation map holds it: strings, because that
 * is what the field-value layer stores. Nothing upstream sanitises them.
 *
 * The operator never types these — picking a lot derives its share — but the
 * map is still string-keyed, so parsing has to stay defensive.
 */
export type LotAllocations = Record<string, string>

/** What the allocator needs to know about a lot's stock. */
export interface LotStock {
  lot: string
  expiry: string | null
  /** What this lot may still commit to a new step. */
  available: number
  /** Shelf count and other runs' holds, when the payload carried them. */
  quantity?: number
  reservedQuantity?: number
  /**
   * A fact about the RECEIPT, not the catalogue entry — mirrors
   * `ApiLot.gateStatus` on the backend. Only `'quarantine'` is excluded from
   * `offeredLots`/`selectableTotal`, matching the same eligibility rule the
   * FEFO proposal already applies server-side (`pick-lists/fefo.ts`); `null`
   * means not gated at all, not blocked.
   */
  gateStatus?: 'quarantine' | 'in_process' | 'available' | null
  locationCode?: string | null
  /** The storage position, when the lot was put away in the hierarchy; shown as its full address. */
  storagePath?: string | null
}

/** Trim float noise from a derived quantity without forcing whole units. */
export function tidyQty(n: number): number {
  return Number(n.toFixed(4))
}

/**
 * Where a batched run's demand came from — "10 × 100 units".
 *
 * The server hands back quantities already multiplied by the procedure's batch
 * size, so a kit run of 100 asks for a four-figure number of vials with nothing
 * on screen accounting for it. This is that accounting. Null for every ordinary
 * procedure, where the authored figure IS the demand and saying so adds noise.
 */
export function batchHint(
  requirement: { qtyRequiredPerUnit?: number },
  quantityMultiplier: number | undefined,
): string | null {
  if (!quantityMultiplier || quantityMultiplier <= 1) return null
  if (requirement.qtyRequiredPerUnit === undefined) return null
  return `${tidyQty(requirement.qtyRequiredPerUnit)} × ${quantityMultiplier} units`
}

/**
 * Sum a material's allocations.
 *
 * Blanks, negatives and junk count as nothing rather than subtracting: a
 * malformed entry must not quietly reduce a total that other lots contributed
 * to correctly.
 */
export function allocatedQty(allocations: LotAllocations | undefined): number {
  if (!allocations) return 0
  return Object.values(allocations).reduce((sum, v) => {
    const n = Number(v)
    return sum + (Number.isFinite(n) && n > 0 ? n : 0)
  }, 0)
}

/**
 * Whether a material's allocations meet its required quantity.
 *
 * Compared with a tolerance because quantities are decimals: 0.1 + 0.2 is not
 * 0.3 in binary floating point, and an operator who has allocated exactly what
 * was asked must not be told they are short by 5.5e-17.
 */
export function isFullyAllocated(
  qtyRequired: number,
  allocations: LotAllocations | undefined,
): boolean {
  return Math.abs(allocatedQty(allocations) - qtyRequired) < 1e-6
}

/** How much of the requirement is still unallocated. Never negative. */
export function outstandingQty(
  qtyRequired: number,
  allocations: LotAllocations | undefined,
): number {
  return Math.max(0, qtyRequired - allocatedQty(allocations))
}

/**
 * What picking this lot would take: as much of the outstanding amount as the
 * lot can cover.
 *
 * This is why the operator never types a quantity — the total comes out right
 * by construction, and a requirement spanning two lots is served by picking a
 * second one.
 */
export function takeFromLot(lot: LotStock, outstanding: number): number {
  return tidyQty(Math.min(Math.max(0, outstanding), Math.max(0, lot.available)))
}

/**
 * The lots a requirement puts on offer.
 *
 * Expired stock is not offered at all — on any process step, not just kit runs.
 * Planning a step against material that is past its date is a deviation rather
 * than a choice, and a row that can be ticked reads as a row that may be.
 * Filtering beats striking through: a list of dead rows buries the lots that
 * can actually be picked, and the operator still has to work out which is
 * which. Quarantined stock is dropped for the same reason and the same
 * exception — the write path (`reserveInventoryFor`, backend) refuses it
 * outright, so a row that could be ticked here would only earn a 400.
 *
 * The one exception, for both expired and quarantined, is a lot this execution
 * has ALREADY drawn from. A step can start before a lot's date (or gate
 * status) turns and end after, so dropping that row would leave its quantity
 * in the requirement's total with nothing on screen accounting for it — and
 * nothing to unpick.
 */
export function offeredLots(
  lots: LotStock[],
  isExpired: (expiry: string | null) => boolean,
  isRecorded: (lot: string) => boolean,
): LotStock[] {
  return lots.filter(
    l => (!isExpired(l.expiry) && l.gateStatus !== 'quarantine') || isRecorded(l.lot),
  )
}

/**
 * Total stock that could still be picked for a requirement.
 *
 * Lets the panel say "there is not enough of this material" outright instead of
 * leaving the operator clicking lots that each refuse to close the gap.
 *
 * Expired and quarantined lots never count — including the already-drawn ones
 * `offeredLots` keeps listed. Those are on the record, not on offer, and
 * counting them would promise stock the step is not allowed to plan against.
 */
export function selectableTotal(
  lots: LotStock[],
  isExpired: (expiry: string | null) => boolean,
): number {
  return lots.reduce(
    (sum, l) => (isExpired(l.expiry) || l.gateStatus === 'quarantine' ? sum : sum + Math.max(0, l.available)),
    0,
  )
}

/**
 * First-expiring first, which is how stock is supposed to leave a shelf.
 *
 * Lots with no expiry sort last rather than first: an undated lot is not
 * urgent, and floating it to the top would push the ones that actually are out
 * of view on a long list.
 *
 * This orders what the operator SEES. It does not allocate — picking follows
 * click order, because which lot leaves the shelf is their call against the
 * physical stock in front of them.
 */
export function byExpiryThenLot(a: LotStock, b: LotStock): number {
  if (a.expiry && b.expiry) return a.expiry.localeCompare(b.expiry) || a.lot.localeCompare(b.lot)
  if (a.expiry) return -1
  if (b.expiry) return 1
  return a.lot.localeCompare(b.lot)
}

/**
 * An expiry as a date, not an instant.
 *
 * The lot record carries a full ISO timestamp, which rendered raw as
 * "exp 2026-12-31T00:00:00.000Z" — a wall of precision for a value that is only
 * ever a day. Sliced rather than parsed through `Date` so the date shown is the
 * one stored, not the one the viewer's timezone shifts it to.
 */
export function expiryDate(expiry: string): string {
  return expiry.slice(0, 10)
}

/** `5 mL`, or plain `5` for a material tracked without a unit. */
export function formatQty(qty: number, uom: string | null | undefined): string {
  const n = String(tidyQty(qty))
  return uom ? `${n} ${uom}` : n
}

// ─── Confirm-and-sign summary ────────────────────────────────────────────────

export interface EquipmentLine {
  requirementId: string
  /** The equipment TYPE, e.g. "Centrifuge". */
  label: string
  /** The chosen unit, or null where the operator picked none. */
  unit: string | null
}

export interface InventoryLine {
  requirementId: string
  label: string
  lots: Array<{ lot: string; qty: number }>
  allocated: number
  required: number
  uom: string | null
}

export interface ReservationSummary {
  equipment: EquipmentLine[]
  inventory: InventoryLine[]
  /**
   * What signing does to the stock listed above, in the operator's terms.
   * `null` where no material is being committed, so the screen says nothing
   * rather than describing an effect on an empty list.
   */
  stockEffect: string | null
}

/**
 * What signing this transition will put on the record.
 *
 * Built from what is on screen rather than from the stored execution: at the
 * moment of confirming, the operator's unsaved edits ARE what they are
 * attesting to, and summarising the server's older copy would show them
 * something other than what they are about to sign.
 */
export function reservationSummary({
  mode,
  connection,
  selections,
  lotAllocations,
  equipmentTypeName,
  equipmentUnitName,
}: {
  mode: TransitionMode
  connection: ProcessStepConnection
  selections: Record<string, string>
  lotAllocations: Record<string, LotAllocations>
  equipmentTypeName: (equipmentTypeId: string) => string
  equipmentUnitName: (equipmentId: string) => string
}): ReservationSummary {
  const equipment: EquipmentLine[] = connection.equipmentRequirements.map(r => ({
    requirementId: r.id,
    label: equipmentTypeName(r.equipmentTypeId),
    unit: selections[r.id] ? equipmentUnitName(selections[r.id]) : null,
  }))

  const inventory: InventoryLine[] = connection.inventoryRequirements
    .map(r => {
      const lots = Object.entries(lotAllocations[r.id] ?? {})
        .map(([lot, v]) => ({ lot, qty: Number(v) }))
        .filter(l => Number.isFinite(l.qty) && l.qty > 0)
      return {
        requirementId: r.id,
        label: r.itemLabel ?? r.consumableId,
        lots,
        allocated: tidyQty(lots.reduce((sum, l) => sum + l.qty, 0)),
        required: r.qtyRequired,
        uom: r.uom,
      }
    })
    // A material with nothing against it is not part of what is being signed.
    .filter(line => line.lots.length > 0)

  return { equipment, inventory, stockEffect: stockEffect(mode, inventory.length > 0) }
}

/**
 * The consequence sentence for the stock being committed.
 *
 * Start places a hold and End settles it — `reserveInventoryFor` and its
 * counterpart in the backend's process-step executions service. Complete moves
 * no stock at all, so it claims nothing.
 */
function stockEffect(mode: TransitionMode, hasInventory: boolean): string | null {
  if (!hasInventory) return null
  if (mode === 'start') return 'This stock is reserved for the run when you sign.'
  if (mode === 'end') return 'This stock is consumed from the lots above when you sign.'
  return null
}
